/**
 * Drawing-job trigger + status + download links.
 *
 * Renders next to the existing client-side DrawingSheet. The DrawingSheet
 * remains the immediate preview; this component is the bridge to the
 * higher-quality TechDraw output served by the B6.4 Cloud Function.
 *
 * Status pill semantics:
 *   - none | idle      → "Generate manufacturing drawing" CTA
 *   - queued | running → "Generating…" with disabled trigger
 *   - succeeded        → download links + "Regenerate" secondary CTA
 *   - failed           → red error banner + "Retry" CTA
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useLatestDrawingJob } from '@/hooks/useLatestDrawingJob';
import { triggerRenderDrawing } from '@/services/drawingService';
import { useLocale } from '@/contexts/LocaleContext';
import type {
  DrawingJob,
  DrawingValidationIssue,
  ParametricArtifact,
  IntakeSpec,
} from '@shared/types';

type Props = {
  conversationId: string;
  messageId: string;
  artifact: ParametricArtifact;
  /** Drawing preferences (from the conversation's intake). Optional. */
  drawingPrefs?: IntakeSpec['drawing'];
  /** Automatically request a TechDraw render when no drawing job exists yet. */
  autoGenerate?: boolean;
  /** Resolved Storage download URL for the latest backend SVG preview. */
  onPreviewUrlChange?: (url: string | null) => void;
};

const FORMATS_DEFAULT: Array<'svg' | 'pdf' | 'dxf'> = ['svg', 'pdf'];

export function DrawingJobControls(props: Props) {
  const { t } = useTranslation('common');
  const {
    conversationId,
    messageId,
    artifact,
    drawingPrefs,
    autoGenerate = false,
    onPreviewUrlChange,
  } = props;
  const { locale } = useLocale();
  const jobState = useLatestDrawingJob(conversationId, messageId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoTriggeredKeyRef = useRef<string | null>(null);
  const previousRenderFingerprintRef = useRef<string | null>(null);

  const job: DrawingJob | null =
    jobState.status === 'ready' ? jobState.job : null;
  const renderFingerprint = useMemo(
    () =>
      fingerprintDrawingInput({
        spec: artifact.spec,
        parameters: artifact.parameters,
        drawingPrefs,
        locale,
      }),
    [artifact.parameters, artifact.spec, drawingPrefs, locale],
  );
  const jobMatchesCurrentInput = job?.renderFingerprint === renderFingerprint;
  const currentJob = jobMatchesCurrentInput ? job : null;
  const status = currentJob?.status;
  const latestJobIsPending = job?.status === 'queued' || job?.status === 'running';
  const svgStoragePath =
    currentJob?.status === 'succeeded' && currentJob.files?.svg
      ? currentJob.files.svg
      : null;

  const handleTrigger = useCallback(async () => {
    if (busy) return;
    if (!artifact.spec) {
      setError('No DesignSpec on artifact — cannot render');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await triggerRenderDrawing({
        conversationId,
        messageId,
        artifactId: artifact.title,
        spec: artifact.spec as unknown as Record<string, unknown>,
        drawing: {
          ...((drawingPrefs as unknown as Record<string, unknown> | undefined) ?? {}),
          locale,
        },
        outputs: FORMATS_DEFAULT,
        renderFingerprint,
      });
    } catch (err) {
      setError((err as Error).message ?? String(err));
    } finally {
      setBusy(false);
    }
  }, [artifact, conversationId, messageId, drawingPrefs, busy, locale, renderFingerprint]);

  const isPending = busy || status === 'queued' || status === 'running';

  useEffect(() => {
    let cancelled = false;
    if (!svgStoragePath || !onPreviewUrlChange) {
      onPreviewUrlChange?.(null);
      return () => {
        cancelled = true;
      };
    }

    getDownloadURL(storageRef(storage, svgStoragePath))
      .then((url) => {
        if (!cancelled) {
          onPreviewUrlChange?.(url);
        }
      })
      .catch((err) => {
        console.error('[DrawingJobControls] Failed to resolve SVG preview URL:', err);
        if (!cancelled) {
          onPreviewUrlChange?.(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [svgStoragePath, onPreviewUrlChange]);

  useEffect(() => {
    if (!autoGenerate || busy || !artifact.spec) return;
    const needsCurrentJob =
      jobState.status === 'none' ||
      (jobState.status === 'ready' && !jobMatchesCurrentInput && !latestJobIsPending);
    if (!needsCurrentJob) return;

    const autoKey = `${conversationId}:${messageId}:${renderFingerprint}`;
    if (autoTriggeredKeyRef.current === autoKey) return;

    autoTriggeredKeyRef.current = autoKey;
    void handleTrigger();
  }, [
    autoGenerate,
    artifact.spec,
    busy,
    conversationId,
    handleTrigger,
    jobMatchesCurrentInput,
    jobState.status,
    latestJobIsPending,
    messageId,
    renderFingerprint,
  ]);

  useEffect(() => {
    if (!autoGenerate || !artifact.spec) {
      previousRenderFingerprintRef.current = renderFingerprint;
      return;
    }

    if (previousRenderFingerprintRef.current === null) {
      previousRenderFingerprintRef.current = renderFingerprint;
      return;
    }

    if (previousRenderFingerprintRef.current === renderFingerprint) {
      return;
    }

    previousRenderFingerprintRef.current = renderFingerprint;
    onPreviewUrlChange?.(null);

    if (!busy && !latestJobIsPending) {
      const autoKey = `${conversationId}:${messageId}:${renderFingerprint}`;
      autoTriggeredKeyRef.current = autoKey;
      void handleTrigger();
    }
  }, [
    artifact.spec,
    autoGenerate,
    busy,
    conversationId,
    handleTrigger,
    latestJobIsPending,
    messageId,
    onPreviewUrlChange,
    renderFingerprint,
  ]);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-synergy-neutral-700 bg-synergy-bg-secondary-dark p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-synergy-text-primary">
            {t('drawing.manufacturingDrawing')}
          </span>
          <StatusPill status={status} jobError={currentJob?.errorMessage} />
        </div>
        <button
          type="button"
          onClick={handleTrigger}
          disabled={isPending}
          className="rounded-full bg-synergy-blue px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {status === 'succeeded'
            ? t('drawing.regenerate')
            : isPending
              ? t('drawing.generating')
              : t('drawing.generate')}
        </button>
      </div>

      {error && (
        <div className="rounded-md border-2 border-brand-indigo bg-brand-cream px-2 py-1 text-xs font-semibold text-brand-indigo">
          {error}
        </div>
      )}

      {currentJob && currentJob.status === 'failed' && currentJob.errorMessage && (
        <div className="rounded-md border-2 border-brand-indigo bg-brand-cream px-2 py-1 text-xs font-semibold text-brand-indigo">
          {currentJob.errorMessage}
        </div>
      )}

      {currentJob && currentJob.status === 'succeeded' && currentJob.files && (
        <DownloadLinks files={currentJob.files} title={artifact.title || 'drawing'} />
      )}

      {currentJob && currentJob.status === 'succeeded' && currentJob.violations && currentJob.violations.length > 0 && (
        <ViolationsList
          violations={currentJob.violations}
          strictAccepted={currentJob.strictAccepted ?? true}
        />
      )}
    </div>
  );
}

function fingerprintDrawingInput(input: {
  spec: unknown;
  parameters: unknown;
  drawingPrefs: unknown;
  locale: string;
}): string {
  try {
    return JSON.stringify(input);
  } catch {
    return `${input.locale}:unserializable`;
  }
}

function ViolationsList({
  violations,
  strictAccepted,
}: {
  violations: DrawingValidationIssue[];
  strictAccepted: boolean;
}) {
  const { t } = useTranslation('common');
  const errors = violations.filter((v) => v.severity === 'error');
  const warnings = violations.filter((v) => v.severity === 'warning');
  const infos = violations.filter((v) => v.severity === 'info');

  return (
    <div className="flex flex-col gap-1 pt-1">
      {!strictAccepted && (
        <div className="text-[10px] font-bold text-brand-indigo">
          {t('drawing.strictModeError', { count: errors.length })}
        </div>
      )}
      {[...errors, ...warnings, ...infos].map((v, i) => (
        <ViolationRow key={`${v.rule}-${i}`} issue={v} />
      ))}
    </div>
  );
}

function ViolationRow({ issue }: { issue: DrawingValidationIssue }) {
  const tone =
    issue.severity === 'error'
      ? 'border-brand-indigo bg-brand-cream text-brand-indigo'
      : issue.severity === 'warning'
        ? 'border-brand-indigo/40 bg-brand-teal-soft/50 text-brand-indigo'
        : 'border-k-line bg-k-card text-brand-indigo/70';
  // Per rev 3.2 §13.1: per-callout granularity. Surface every id the
  // backend emits as small monospace chips so the user can pin the
  // failing object without scanning the message text.
  const chips: { kind: 'feature' | 'callout' | 'part' | 'datum'; id: string }[] = [];
  if (issue.featureId) chips.push({ kind: 'feature', id: issue.featureId });
  if (issue.calloutId) chips.push({ kind: 'callout', id: issue.calloutId });
  if (issue.partId) chips.push({ kind: 'part', id: issue.partId });
  if (issue.datumLabel) chips.push({ kind: 'datum', id: issue.datumLabel });
  return (
    <div className={`rounded border px-2 py-1 text-[10px] ${tone}`}>
      <div className="font-medium">{issue.rule}</div>
      <div>{issue.message}</div>
      {chips.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {chips.map((c, i) => (
            <span
              key={`${c.kind}-${c.id}-${i}`}
              className="rounded bg-brand-indigo/10 px-1.5 py-0.5 font-mono text-[9px] tracking-wide text-brand-indigo"
              title={`${c.kind} id`}
            >
              <span className="opacity-60">{c.kind}:</span>
              {c.id}
            </span>
          ))}
        </div>
      )}
      {issue.suggestion && (
        <div className="opacity-70 mt-0.5">→ {issue.suggestion}</div>
      )}
    </div>
  );
}

function StatusPill({
  status,
  jobError,
}: {
  status?: DrawingJob['status'];
  jobError?: string;
}) {
  const { t } = useTranslation('common');
  if (!status) return null;
  const map: Record<DrawingJob['status'], { label: string; cls: string }> = {
    queued: { label: t('drawing.statusQueued'), cls: 'bg-brand-cream text-brand-indigo border border-brand-indigo/30' },
    running: { label: t('drawing.statusRunning'), cls: 'bg-brand-teal-soft text-brand-indigo' },
    succeeded: { label: t('drawing.statusReady'), cls: 'bg-brand-teal text-brand-indigo' },
    failed: { label: t('drawing.statusFailed'), cls: 'bg-brand-indigo text-brand-cream' },
  };
  const m = map[status];
  return (
    <span
      title={jobError ?? undefined}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function DownloadLinks({
  files,
  title,
}: {
  files: Partial<Record<'svg' | 'pdf' | 'dxf', string>>;
  title: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      {(['pdf', 'svg', 'dxf'] as const).map((fmt) => {
        const path = files[fmt];
        if (!path) return null;
        return (
          <DownloadLink
            key={fmt}
            format={fmt}
            storagePath={path}
            filename={`${title}.${fmt}`}
          />
        );
      })}
    </div>
  );
}

function DownloadLink({
  format,
  storagePath,
  filename,
}: {
  format: 'svg' | 'pdf' | 'dxf';
  storagePath: string;
  filename: string;
}) {
  const [busy, setBusy] = useState(false);
  const onClick = useCallback(async () => {
    setBusy(true);
    try {
      const url = await getDownloadURL(storageRef(storage, storagePath));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setBusy(false);
    }
  }, [storagePath, filename]);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="rounded border border-synergy-neutral-500 bg-synergy-bg-dark px-2 py-0.5 text-[10px] uppercase tracking-wider text-synergy-text-secondary hover:border-synergy-blue hover:text-synergy-blue disabled:opacity-50"
    >
      {format}
    </button>
  );
}
